/**
 * 列表增删的平滑补位（手写 FLIP）与元素退场。
 *
 * 为什么自己写 FLIP：
 *   元素被删除后没有过渡可言，CSS 做不了"剩下的卡片补位"这个效果。
 *   GSAP 的 Flip 插件最省事，但那是一个额外插件依赖。
 *   这里手写约 50 行，逻辑看得见，也少一个依赖。
 *
 * 原理（First-Last-Invert-Play）：
 *   1. 记下每个元素上一次的位置（First）
 *   2. 本轮渲染后读一次新位置（Last）
 *   3. 立刻用 transform 把元素挪回旧位置（Invert）
 *   4. 把 transform 归零，浏览器自己补出平滑位移（Play）
 *
 * 位置用 offsetTop/offsetLeft 而不是 getBoundingClientRect：
 * 前者不受页面滚动影响，滚动时不会误触发动画。
 */

import { useLayoutEffect, useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

interface Position {
	top: number;
	left: number;
}

export function useListFlip<T extends HTMLElement = HTMLElement>(
	deps: unknown[],
	options: { attr?: string; duration?: number } = {},
): RefObject<T> {
	const containerRef = useRef<T | null>(null);
	const previous = useRef<Map<string, Position>>(new Map());
	const { attr = 'data-flip-id', duration = MOTION.flip } = options;

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const elements = Array.from(container.querySelectorAll<HTMLElement>(`[${attr}]`));

		const current = new Map<string, Position>();
		const moving: Array<{ element: HTMLElement; dx: number; dy: number }> = [];

		for (const element of elements) {
			const id = element.getAttribute(attr);
			if (!id) continue;

			const position: Position = { top: element.offsetTop, left: element.offsetLeft };
			current.set(id, position);

			const before = previous.current.get(id);
			if (!before) continue;

			const dy = before.top - position.top;
			const dx = before.left - position.left;
			if (Math.abs(dy) > 1 || Math.abs(dx) > 1) moving.push({ element, dx, dy });
		}

		previous.current = current;

		if (moving.length === 0 || prefersReducedMotion()) return;

		for (const { element, dx, dy } of moving) {
			gsap.fromTo(
				element,
				{ x: dx, y: dy },
				{
					x: 0,
					y: 0,
					duration,
					ease: MOTION.ease.out,
					overwrite: 'auto',
					clearProps: 'transform',
				},
			);
		}

		return () => {
			for (const { element } of moving) gsap.killTweensOf(element);
		};
	}, deps);

	return containerRef as RefObject<T>;
}

/**
 * 让一个元素从列表中"消失"后再真正移除。
 *
 * 用法：先播动画，动画结束的回调里再改 React 状态把数据删掉。
 * 顺序反了就没有动画可播，因为元素已经不在 DOM 里了。
 */
export function animateExit(
	element: HTMLElement | null,
	onDone: () => void,
	direction: 1 | -1 = 1,
): void {
	if (!element || prefersReducedMotion()) {
		onDone();
		return;
	}

	// 先固定成像素高度，才能从"当前高度"平滑塌陷到 0
	const rect = element.getBoundingClientRect();
	gsap.set(element, { height: rect.height, overflow: 'hidden' });

	gsap
		.timeline({
			onComplete: () => {
				gsap.set(element, { clearProps: 'all' });
				onDone();
			},
		})
		.to(element, {
			x: direction * 64,
			autoAlpha: 0,
			duration: MOTION.exit,
			ease: MOTION.ease.in,
		}, 0)
		.to(
			element,
			{ height: 0, marginTop: 0, marginBottom: 0, duration: 0.2, ease: 'power2.inOut' },
			MOTION.exit * 0.7,
		);
}

/** 批量错开消失（多选删除时用） */
export function animateExitBatch(elements: HTMLElement[], onDone: () => void): void {
	if (elements.length === 0 || prefersReducedMotion()) {
		onDone();
		return;
	}
	gsap.to(elements, {
		x: 64,
		autoAlpha: 0,
		duration: MOTION.exit,
		stagger: 0.028,
		ease: MOTION.ease.in,
		onComplete: onDone,
	});
}
